import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueries } from 'react-query';
import { Main as Container } from 'Components/Main';
import { PageHeader } from 'Components/ui';
import { useTranslation } from 'react-i18next';
import { PageLoaderList } from 'Components/PageLoader/List';
import { NoData } from 'Components/NoData';
import { DefaultTable } from 'Components/common/DefaultTable';
import { DefaultPagination } from 'Components/common/DefaultPagination';
import { StatusChip } from 'Components/common/StatusChip';
import { UsageBar } from 'Components/UsageBar';
import styled from 'styled-components';
import { getOpsDashboard } from 'services/company-service';
import { getPolicyMetrics } from 'services/policies-service';
import { getGaMetrics } from 'services/gap-analysis';
import { getUserMetrics } from 'services/users-service';
import { getCookieMetrics } from 'services/cookies-service';

const SummaryCardWrapper = styled.div`
  flex: 1;
  border: 1px solid var(--vo-cloud);
  border-radius: var(--radius-lg);
  background: white;
  padding: 16px;
  text-align: center;
`;

const SummaryValue = styled.span`
  font-size: 2rem;
  font-weight: 700;
  display: block;
`;

const SummaryLabel = styled.span`
  font-size: 14px;
  color: var(--vo-slate);
`;

const SummaryCard = ({ label, value, color }) => (
  <SummaryCardWrapper>
    <SummaryValue style={{ color: color ?? 'var(--vo-ink)' }}>
      {value ?? '—'}
    </SummaryValue>
    <SummaryLabel>{label}</SummaryLabel>
  </SummaryCardWrapper>
);

const SummaryRow = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 24px;
  margin-bottom: 24px;
`;

const ErrorMessage = styled.span`
  color: var(--vo-danger);
`;

const PaginationRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 16px;
`;

const TenantName = styled.span`
  font-size: 14px;
  font-weight: 600;
`;

const TenantCaption = styled.span`
  font-size: 12px;
  color: var(--vo-slate);
`;

export const OpsDashboard = () => {
  const { t } = useTranslation(['common']);
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  const { isLoading, error, data: dashboard } = useQuery(
    ['ops-dashboard', page],
    () => getOpsDashboard(page, 20).then(r => r.data)
  );

  useEffect(() => {
    if (error && error.response?.status === 403) {
      navigate('/no-permission');
    }
  }, [error, navigate]);

  const tenants = dashboard?.data ?? [];

  const metricsQueries = useQueries(
    tenants.map(tenant => ({
      queryKey: ['ops-metrics', tenant.slug],
      queryFn: () =>
        Promise.all([
          getPolicyMetrics(tenant.slug).then(r => r.data).catch(() => ({ totalPolicies: null })),
          getGaMetrics(tenant.slug).then(r => r.data).catch(() => ({ totalFrameworks: null })),
          getUserMetrics(tenant.slug).then(r => r.data).catch(() => ({ totalUsers: null })),
          getCookieMetrics(tenant.slug).then(r => r.data).catch(() => ({ totalDisclaimers: null })),
        ]),
      staleTime: 5 * 60 * 1000,
    }))
  );

  const getMetricsForTenant = (idx) => {
    const q = metricsQueries[idx];
    if (!q?.data) return { policy: null, ga: null, users: null, cookies: null };
    const [policy, ga, users, cookies] = q.data;
    return { policy, ga, users, cookies };
  };

  const summary = dashboard?.summary;

  const tableRows = [
    { id: 'name', value: 'Nome', render: (val, item) => (
      <><TenantName>{item.name}</TenantName><br /><TenantCaption>{item.slug}</TenantCaption></>
    )},
    { id: 'signedPlan', value: 'Plano', render: (val) => <StatusChip label={val || '—'} variant="default" /> },
    { id: 'active', value: 'Status', render: (val) => <StatusChip label={val ? 'Ativo' : 'Inativo'} variant={val ? 'success' : 'default'} /> },
    { id: 'policies', value: 'Políticas', render: (val, item, idx) => {
      const { policy } = getMetricsForTenant(idx);
      return <UsageBar current={policy?.totalPolicies ?? 0} limit={item.planLimits?.maxPoliciesLimit} />;
    }},
    { id: 'ga', value: 'Gap Analysis', render: (val, item, idx) => {
      const { ga } = getMetricsForTenant(idx);
      return <UsageBar current={ga?.totalFrameworks ?? 0} limit={item.planLimits?.maxGapAnalysisLimit} />;
    }},
    { id: 'users', value: 'Usuários', render: (val, item, idx) => {
      const { users } = getMetricsForTenant(idx);
      return <UsageBar current={users?.totalUsers ?? 0} limit={item.planLimits?.maxUsersLimit} />;
    }},
    { id: 'cookies', value: 'Cookies', render: (val, item, idx) => {
      const { cookies } = getMetricsForTenant(idx);
      return <span style={{ fontSize: '14px' }}>{cookies?.totalDisclaimers ?? '—'}</span>;
    }},
  ];

  return (
    <Container>
      <PageHeader
        title={t('common:sidebar.opsDashboard')}
        segments={[
          { label: t('common:breadcrumbs.generalSettings') },
          { label: t('common:sidebar.opsDashboard') },
        ]}
      />
      {isLoading && <PageLoaderList />}
      {error && <ErrorMessage>Erro ao carregar dados.</ErrorMessage>}

      {!isLoading && !error && summary && (
        <>
          <SummaryRow>
            <SummaryCard label="Total Ativos" value={summary.totalActive} color={'var(--vo-success)'} />
            <SummaryCard label="Total Inativos" value={summary.totalInactive} color={'var(--vo-steel)'} />
            <SummaryCard label="Free" value={summary.planDistribution?.free ?? 0} />
            <SummaryCard label="Starter" value={summary.planDistribution?.starter ?? 0} />
            <SummaryCard label="Premium" value={summary.planDistribution?.premium ?? 0} />
          </SummaryRow>

          {tenants.length === 0 ? (
            <NoData description="Nenhum tenant encontrado" />
          ) : (
            <>
              <DefaultTable
                rows={tableRows}
                cells={tenants}
                hideShow
                hideEdit
                hideDelete
              />

              <PaginationRow>
                <DefaultPagination
                  count={dashboard.totalPages}
                  page={page}
                  onChange={(_, p) => setPage(p)}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </PaginationRow>
            </>
          )}
        </>
      )}
    </Container>
  );
};
